import { useState, useCallback } from 'react';
import {
  GripVertical, ChevronDown, ChevronRight, CornerDownRight,
  X, Pencil, Trash2
} from 'lucide-react';

export function buildTree(codes) {
  const map = {};
  const roots = [];
  codes.forEach(c => { map[c.code] = { ...c, children: [] }; });
  codes.forEach(c => {
    const node = map[c.code];
    if (c.parent_code && map[c.parent_code]) {
      map[c.parent_code].children.push(node);
    } else {
      roots.push(node);
    }
  });
  return roots;
}

function flattenTree(roots) {
  const result = [];
  const walk = (nodes, depth) => {
    nodes.forEach(n => {
      result.push({ ...n, depth, hasChildren: n.children.length > 0 });
      walk(n.children, depth + 1);
    });
  };
  walk(roots, 0);
  return result;
}

export function HierarchyTreeView({ codes, onDrop, onRemoveParent, onDelete, onEdit }) {
  const [collapsed, setCollapsed] = useState({});
  const [draggedCode, setDraggedCode] = useState(null);
  const [dragOverCode, setDragOverCode] = useState(null);

  const tree = buildTree(codes);
  const flat = flattenTree(tree);

  // Filter out collapsed children
  const visible = [];
  const hiddenParents = new Set();
  for (const node of flat) {
    if (node.parent_code && hiddenParents.has(node.parent_code)) {
      if (node.hasChildren) hiddenParents.add(node.code);
      continue;
    }
    if (collapsed[node.code] && node.hasChildren) {
      hiddenParents.add(node.code);
    }
    visible.push(node);
  }

  const toggleCollapse = useCallback((code) => {
    setCollapsed(p => ({ ...p, [code]: !p[code] }));
  }, []);

  return (
    <div data-testid="hierarchy-tree">
      <div className="flex items-center gap-1 py-1.5 px-2 bg-[#F9FAFB] border-b border-[#E5E7EB]">
        <span className="w-3 shrink-0" />
        <span className="w-4 shrink-0" />
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF] min-w-[60px]">Code</span>
        <span className="text-[10px] uppercase tracking-[0.2em] font-semibold text-[#9CA3AF] flex-1">Label</span>
      </div>
      {visible.map(node => {
        const isDragOver = dragOverCode === node.code && draggedCode !== node.code;
        const bg = isDragOver ? 'bg-[#002FA7]/10' : 'hover:bg-[#F9FAFB]';
        const padLeft = node.depth * 24 + 8;

        return (
          <div
            key={node.code}
            className={`flex items-center gap-1 py-1.5 px-2 border-b border-[#E5E7EB] transition-colors duration-100 ${bg}`}
            style={{ paddingLeft: padLeft + 'px' }}
            draggable
            onDragStart={(e) => { e.stopPropagation(); setDraggedCode(node.code); e.dataTransfer.effectAllowed = 'move'; }}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); if (draggedCode && draggedCode !== node.code) setDragOverCode(node.code); }}
            onDragLeave={(e) => { e.stopPropagation(); setDragOverCode(null); }}
            onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragOverCode(null); if (draggedCode && draggedCode !== node.code) { onDrop(draggedCode, node.code); setDraggedCode(null); } }}
            onDragEnd={() => { setDraggedCode(null); setDragOverCode(null); }}
            data-testid={`tree-node-${node.code}`}
          >
            <GripVertical className="w-3 h-3 text-[#9CA3AF] cursor-grab shrink-0" />
            {node.hasChildren ? (
              <button onClick={() => toggleCollapse(node.code)} className="w-4 h-4 flex items-center justify-center shrink-0" data-testid={`toggle-${node.code}`}>
                {collapsed[node.code] ? <ChevronRight className="w-3 h-3 text-[#4B5563]" /> : <ChevronDown className="w-3 h-3 text-[#4B5563]" />}
              </button>
            ) : (
              <span className="w-4 shrink-0" />
            )}
            {node.depth > 0 && <CornerDownRight className="w-3 h-3 text-[#9CA3AF] shrink-0" />}
            <span className="text-xs font-semibold text-[#0A0A0A] min-w-[60px]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{node.code}</span>
            <span className="text-sm text-[#4B5563] flex-1 truncate">{node.label}</span>
            {node.parent_code && (
              <button onClick={() => onRemoveParent(node.code)} className="text-[10px] px-1.5 py-0.5 bg-zinc-100 text-zinc-500 hover:bg-red-50 hover:text-red-600 flex items-center gap-0.5" data-testid={`unparent-${node.code}`}>
                <X className="w-2.5 h-2.5" /> parent
              </button>
            )}
            <button onClick={() => onEdit(node)} className="w-5 h-5 flex items-center justify-center text-[#9CA3AF] hover:text-[#4B5563]" data-testid={`edit-code-${node.code}`}>
              <Pencil className="w-3 h-3" />
            </button>
            <button onClick={() => onDelete(node.code)} className="w-5 h-5 flex items-center justify-center text-[#9CA3AF] hover:text-red-500" data-testid={`delete-code-${node.code}`}>
              <Trash2 className="w-3 h-3" />
            </button>
          </div>
        );
      })}
      {draggedCode && (
        <RootDropZoneInner draggedCode={draggedCode} onDropToRoot={(code) => { onDrop(code, null); setDraggedCode(null); }} />
      )}
      {codes.length === 0 && (
        <div className="py-12 text-center text-sm text-[#9CA3AF]">No codes yet. Add manually or import CSV.</div>
      )}
    </div>
  );
}

function RootDropZoneInner({ draggedCode, onDropToRoot }) {
  const [over, setOver] = useState(false);
  const cls = over ? 'border-[#002FA7] bg-[#002FA7]/5 text-[#002FA7]' : 'border-[#E5E7EB] text-[#9CA3AF]';
  return (
    <div
      className={`border-2 border-dashed py-3 text-center text-xs transition-colors ${cls}`}
      onDragOver={e => { e.preventDefault(); setOver(true); }}
      onDragLeave={() => setOver(false)}
      onDrop={e => { e.preventDefault(); setOver(false); onDropToRoot(draggedCode); }}
      data-testid="root-drop-zone"
    >
      Drop here to make root-level
    </div>
  );
}
